<script>
import VueSelect from 'vue-select';
import { debounce, capitalize } from 'lodash';
import moment from 'moment';
import PersonSpecReadonly from './PersonSpecReadonly.vue';
import JobProfileReadonly from './JobProfileReadonly.vue';
import RedactorControl from '../RedactorControl.vue';

export default {
    components: {
        VueSelect,
        PersonSpecReadonly,
        JobProfileReadonly,
        RedactorControl
    },

    props: {
        flow: {
            type: String,
            required: true // `write` or `readonly`
        },
        twoColumn: {
            type: Boolean,
            default: false
        },
        compact: {
            Boolean,
            default: false
        },
        creatorId: {
            type: Number,
            required: false,
        },
        requisition: {
            type: Object,
            required: true
        },
        provinces: {
            type: Array,
            required: true
        },
        industries: {
            type: Array,
            required: true
        },
        contractTypes: {
            type: Array,
            required: true
        },
        hideStatus: {
            type: Boolean,
            default: false
        },
        csrfToken: {
            type: String,
            required: false
        },
        errors: {
            type: Object,
            default: () => []
        },
        flatErrors: {
            type: Object,
            default: () => []
        },
        inputsClickable: {
            type: Boolean,
            default: false
        },
        step: {
            type: String,
            default: '1st'
        },
        jobProfileCompact: {
            type: Boolean,
            default: true
        },
        expandedReadonly: {
            type: Boolean,
            default: true
        },
        gridPosition: {
            type: String,
            default: 'items-center'
        },
        questionsClass: {
            type: String,
            default: 'bordered'
        },
        isPipeline: {
            type: Boolean,
            default: false
        },
        isApproval: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            personSpecification: this.requisition.person_specification || null,
            personSpecifications: this.requisition.person_specification ? [this.requisition.person_specification] : [],

            companies: this.requisition.person_specification && this.requisition.person_specification.business_line ? [this.requisition.person_specification.business_line.company] : [],
            company: this.requisition.person_specification && this.requisition.person_specification.business_line ? this.requisition.person_specification.business_line.company : null,

            businessLines: this.requisition.person_specification && this.requisition.person_specification.business_line ? [this.requisition.person_specification.business_line] : [],
            businessLine: this.requisition.person_specification && this.requisition.person_specification.business_line ? this.requisition.person_specification.business_line : null,

            jobProfile: this.requisition.job_profile || null,
            jobProfiles: this.requisition && this.requisition.job_profile ? [this.requisition.job_profile] : [],

            province: this.requisition.province ? this.requisition.province : null,
            contractType: this.requisition.contract_type ? this.requisition.contract_type  : null,
            industry: this.requisition.industry ? this.requisition.industry : null,

            closingDate: this.requisition.closing_date ? moment(new Date(this.requisition.closing_date)).format('YYYY-MM-DD hh:mm:ss') : null,

            users: [
                    this.requisition.primary_recruiter,
                    this.requisition.senior_recruiter,
                    this.requisition.first_approver,
                    this.requisition.second_approver,
                    this.requisition.third_approver,
                ].filter(user => user !== null),
            primaryRecruiter: this.requisition.primary_recruiter || null,
            seniorRecruiter: this.requisition.senior_recruiter || null,

            firstApprover: this.requisition.first_approver || null,
            secondApprover: this.requisition.second_approver || null,
            thirdApprover: this.requisition.third_approver || null,

            marketRelated: this.requisition.is_market_related ? true : false,
            isEvergreen: this.requisition.is_evergreen ? true : false,

            costCenter: this.requisition.cost_center ? this.requisition.cost_center : null,

            costCenters: [],
        }
    },

    methods: {
        removePTag(str) {
            if (str === null || str === undefined) {
                return '';
            }
            return str.replace(/<p>/g, '').replace(/<\/p>/g, '');
        },
        fetchCompanies(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/companies/for-requisition.json?search=${search}&limit=1000`, loading, json => {
                this.companies = json.companies
            });
        },
        fetchBusinessLines(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/business-lines/for-requisition.json?search=${search}&company_id=${this.company.id}&limit=1000`, loading, json => {
                this.businessLines = json.businessLines
            });
        },
        fetchPersonSpecs(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/person-specifications/for-requisition.json?search=${search}&business_line_id=${this.businessLine.id}&full_spec=1&limit=1000`, loading, json => {
                this.personSpecifications = json.personSpecifications
            });
        },
        fetchJobProfiles(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/job-profiles/for-requisition.json?search=${search}&company_id=${this.company.id}&limit=1000`, loading, json => {
                this.jobProfiles = json.jobProfiles.map(profile => Object.assign({}, profile, {
                    full_title_with_external_requisition_number: `${profile.full_title} - ${profile.external_requisition_number}`
                }));
            });
        },
        fetchUsers(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/users/requisition-approvers.json?search=${search}&limit=1000`, loading, json => {
                this.users = json.users
            });
        },
        async fetchUser(id) {
            return await fetch(`/recruiter/users/show.json?id=${id}`)
                .then(res => res.json())
                .then(res => res.user)
        },
        async fetchJobProfile(id) {
            return await fetch(`/recruiter/job-profiles/show.json?id=${id}`)
                .then(res => res.json())
                .then(res => res.jobProfile)
        },
        async fetchPersonSpec(id) {
            return await fetch(`/recruiter/person-specifications/show.json?id=${id}`)
                .then(res => res.json())
                .then(res => res.personSpecification)
        },
        fetchCostCenters(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/recruiter/requisitions/cost-centers.json?search=${search}&limit=1000`, loading, json => {
                this.costCenters = json.costCenters
            });
        },
        search: debounce((url, loading, mapResult) => {
            fetch(url).then(res => {
                res.json().then(mapResult);
                if (loading) loading(false);
            });
        }, 250),
        companyInput() {
            this.jobProfile = null;
            this.businessLine = null;
            this.personSpecification = null;
        },
        businessLineInput() {
            this.personSpecification = null;
        },
        now() {
            return parseInt(Date.now() / 1000) + (2 * 60 * 60);
        },
        saveForm() {
            console.log(this.requisition)
        },
        submit() {
            this.$refs.formSubmitButon.click()
        }
    },

    filters: {
        capitalize(value) {
            return capitalize(value)
        }
    },

    mounted() {
        for (let error in this.errors) {
            console.log(error)
            console.log(this.errors[error])
        }
    }
}
</script>

<template>
    <form class="requisition-crud" method="POST">
        <input v-if="! requisition.parent_id && creatorId" name="creator_id" type="hidden" :value="creatorId" />
        <input :value="isPipeline" name="is_pipeline" type="hidden"/>
        <input :value="isApproval" name="is_approval" type="hidden"/>
        <!-- <div v-if="flatErrors.length > 0" class="errors mb-4 is-invalid-label">
            <ul>
                <li v-for="error in flatErrors" :key="'error' + error">
                    {{ error }}
                </li>
            </ul>
        </div> -->

        <div class="grid-x grid-padding-x items-center mb-3">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">Business Unit<sup>*</sup></label>

                <vue-select
                    label="title"
                    :disabled="flow == 'readonly'"
                    @search="fetchCompanies"
                    @open="fetchCompanies"
                    @input="companyInput"
                    :options="companies"
                    v-model="company"
                    placeholder="eg. Vodacom"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!company"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                    <template slot="option" slot-scope="option">
                        {{ option.title }}
                    </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Business Unit', company.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly' || company === null) ? 'readonly' : ''">Line of Business<sup>*</sup></label>

                <vue-select
                    :disabled="flow == 'readonly' || company === null"
                    label="title"
                    @search="fetchBusinessLines"
                    @open="fetchBusinessLines"
                    @input="businessLineInput"
                    :options="businessLines"
                    v-model="businessLine"
                    placeholder="eg. Billing, Repairs"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!businessLine"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Line of Business', businessLine.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly' || company === null) ? 'readonly' : ''">Job Profile<sup>*</sup></label>

                <input v-if="flow != 'readonly' && jobProfile" name="job_profile_id" type="hidden" :value="jobProfile.id" />

                <vue-select
                    :disabled="flow == 'readonly' || company === null"
                    label="full_title_with_external_requisition_number"
                    @search="fetchJobProfiles"
                    @open="fetchJobProfiles"
                    :options="jobProfiles"
                    v-model="jobProfile"
                    placeholder="Select one"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!jobProfile"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Job Profile', jobProfile.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <job-profile-readonly
            v-if="jobProfile"
            :two-column="twoColumn"
            :compact=jobProfileCompact
            :expand=expandedReadonly
            :job-profile="jobProfile"
            @input-clicked="(title, val) => $emit('input-clicked', title, val)"
            :inputsClickable="inputsClickable"
            :readonly-grid-position="gridPosition"
        />

        <div class="grid-x grid-padding-x my-4 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly' || businessLine === null) ? 'readonly' : ''">Person Specification<sup>*</sup></label>

                <input v-if="flow != 'readonly' && personSpecification" name="person_specification_id" type="hidden" :value="personSpecification.id" />

                <vue-select
                    :disabled="flow == 'readonly' || businessLine === null"
                    label="full_title"
                    @search="fetchPersonSpecs"
                    @open="fetchPersonSpecs"
                    :options="personSpecifications"
                    v-model="personSpecification"
                    placeholder="Select one"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!personSpecification"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Person Specification', personSpecification.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <person-spec-readonly
            v-if="personSpecification"
            :two-column="twoColumn"
            :compact="compact"
            :expand=expandedReadonly
            :person-specification="personSpecification"
            @input-clicked="(title, val) => $emit('input-clicked', title, val)"
            :inputsClickable="inputsClickable"
            :questions-class="questionsClass"
        />

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">Province<sup>*</sup></label>
                <input v-if="province" name="province_id" type="hidden" :value="province.id" />
                <vue-select 
                    :disabled="flow == 'readonly'"
                    label="title"
                    v-model="province"
                    :options="provinces"
                >
                <template #search="{attributes, events}">
                    <input
                        class="vs__search"
                        :required="!province"
                        v-bind="attributes"
                        v-on="events"
                    />
                </template>
                <template slot="option" slot-scope="option">
                    {{ option.title }}
                </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Province', requisition.province.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly' || marketRelated == true) ? 'readonly' : ''">Seats</label>

                <input :disabled="flow == 'readonly'" class="mb-0" name="seats" type="number" step="1" min="0" v-model="requisition.seats" />
                <div v-if="errors['seats'] !== undefined" class="is-invalid-label">
                    <small v-for="error in errors['seats']" :key="'seats'+error"> {{ error }}</small><br/>
                </div>
            </div>

            <div v-if="inputsClickable" class="cell shrink mb-2">
                <div @click="() => $emit('input-clicked', 'Seats', requisition.seats)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>


        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-6'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">Area</label>

                <RedactorControl v-if="flow == 'write'"
                    name="area"
                    :value="(requisition) ? requisition.area : ''"
                    rows="5"
                />

                <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.area)"></div>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Area', requisition.area)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly' || marketRelated == true) ? 'readonly' : ''">Salary<sup>*</sup></label>

                <input :disabled="marketRelated == true || flow == 'readonly'" class="mb-0" required name="salary" type="number" step="500" min="0" v-model="requisition.salary" />
                <div v-if="errors['salary'] !== undefined" class="is-invalid-label">
                    <small v-for="error in errors['salary']" :key="'salary'+error"> {{ error }}</small><br/>
                </div>

                <template v-if="flow == 'write'">
                    <input v-if="marketRelated" name="salary" type="hidden" value="10000" />
                    <input v-else name="is_market_related" value="0" type="hidden" />
                </template>
            </div>

            <div v-if="inputsClickable" class="cell shrink mb-2">
                <div @click="() => $emit('input-clicked', 'Salary', (requisition.is_market_related) ? 'Market Related' : requisition.salary)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">
                    Is Market Related?
                    <input :disabled="flow == 'readonly'" name="is_market_related" value="1" type="checkbox" v-model="marketRelated" @change="event => event.target.checked ? requisition.salary = 10000 : null" />
                </label>
            </div>
        </div>
        
        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">
                    Is Evergreen Job Requisition?
                    <input :disabled="flow == 'readonly'" name="is_evergreen" value="1" type="checkbox" v-model="isEvergreen"/>
                </label>
            </div>
            <div v-if="inputsClickable && isEvergreen" class="cell shrink auto">
                <div @click="() => $emit('input-clicked', 'Evergreen', '')" class="add-button">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div>
            <label :class="twoColumn ? '' : 'low-opacity'">Remuneration Package</label>

            <div class="remuneration-fields">
                <div class="grid-x grid-padding-x mb-3" :class="this.gridPosition">
                    <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                        <label class="fw-normal" :class="flow == 'readonly' ? 'readonly' : ''">Contract Type<sup>*</sup></label>
                        <input v-if="contractType" name="contract_type_id" type="hidden" :value="contractType.id" />
                        <vue-select
                            :disabled="(flow == 'readonly') ? 'disabled' : false"
                            label="title"
                            :options="contractTypes"
                            v-model="contractType"
                        >
                            <template #search="{attributes, events}">
                                <input
                                    class="vs__search"
                                    :required="!contractType"
                                    v-bind="attributes"
                                    v-on="events"
                                />
                            </template>
                        </vue-select>
                    </div>

                    <div v-if="inputsClickable" class="cell shrink">
                        <div @click="() => $emit('input-clicked', 'Contract Type', requisition.contract_type.title)" class="add-button pt-23">
                            <i class="fas fa-plus-circle"></i>
                        </div>
                    </div>
                </div>

                <div v-if="twoColumn">
                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['benefits'] === undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Benefits</label>
                            <label v-if="errors['benefits'] !== undefined" class="is-invalid-label">Benefits</label>
                            <RedactorControl v-if="flow == 'write'"
                                    name="benefits"
                                    :value="(requisition) ? requisition.benefits : ''"
                                    rows="5"
                            />

                            <div v-if="errors['benefits'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['benefits']" :key="'benefits'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.benefits)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Benefits', requisition.benefits)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>

                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['incentives'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Incentives</label>
                            <label v-if="errors['incentives'] !== undefined" class="is-invalid-label">Incentives</label>
                            <RedactorControl v-if="flow == 'write'"
                                name="incentives"
                                :value="(requisition) ? requisition.incentives : ''"
                                rows="5"
                            />
                            <div v-if="errors['incentives'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['incentives']" :key="'incentive'+error"> {{ error }}<br/></small>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.incentives)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Incentives', requisition.incentives)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['bonuses'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Bonuses</label>
                            <label v-if="errors['bonuses'] !== undefined" class="is-invalid-label">Bonuses</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="bonuses"
                                :value="(requisition) ? requisition.bonuses : ''"
                                rows="5"
                            />

                            <div v-if="errors['bonuses'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['bonuses']" :key="'bonuses'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.bonuses)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Bonuses', requisition.bonuses)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>

                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['overtime'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Overtime</label>
                            <label v-if="errors['overtime'] !== undefined" class="is-invalid-label">Overtime</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="overtime"
                                :value="(requisition) ? requisition.overtime : ''"
                                rows="5"
                            />
                            <div v-if="errors['overtime'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['overtime']" :key="'overtime'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.overtime)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Overtime', requisition.overtime)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['public_holidays_and_sundays'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Public Holidays & Sundays</label>
                            <label v-if="errors['public_holidays_and_sundays'] !== undefined" class="is-invalid-label">Public Holidays & Sundays</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="public_holidays_and_sundays"
                                :value="(requisition) ? requisition.public_holidays_and_sundays : ''"
                                rows="5"
                            />
                            <div v-if="errors['public_holidays_and_sundays'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['public_holidays_and_sundays']" :key="'public_holidays_and_sundays'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.public_holidays_and_sundays)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Public Holidays & Sundays', requisition.public_holidays_and_sundays)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>

                        <div class="cell mb-3 medium-4">
                            <label v-if="errors['leave_policy'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Leave</label>
                            <label v-if="errors['leave_policy'] !== undefined" class="is-invalid-label">Leave</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="leave_policy"
                                :value="(requisition) ? requisition.leave_policy : ''"
                                rows="5"
                            />
                            <div v-if="errors['leave_policy'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['leave_policy']" :key="'leave_policy'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.leave_policy)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Leave', requisition.leave_policy)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['benefits'] === undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Benefits</label>
                            <label v-if="errors['benefits'] !== undefined" class="is-invalid-label">Benefits</label>
                            <RedactorControl v-if="flow == 'write'"
                                    name="benefits"
                                    :value="(requisition) ? requisition.benefits : ''"
                                    rows="5"
                            />

                            <div v-if="errors['benefits'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['benefits']" :key="'benefits'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.benefits)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Benefits', requisition.benefits)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['incentives'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Incentives</label>
                            <label v-if="errors['incentives'] !== undefined" class="is-invalid-label">Incentives</label>
                            <RedactorControl v-if="flow == 'write'"
                                name="incentives"
                                :value="(requisition) ? requisition.incentives : ''"
                                rows="5"
                            />
                            <div v-if="errors['incentives'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['incentives']" :key="'incentive'+error"> {{ error }}<br/></small>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.incentives)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Incentives', requisition.incentives)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['bonuses'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Bonuses</label>
                            <label v-if="errors['bonuses'] !== undefined" class="is-invalid-label">Bonuses</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="bonuses"
                                :value="(requisition) ? requisition.bonuses : ''"
                                rows="5"
                            />

                            <div v-if="errors['bonuses'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['bonuses']" :key="'bonuses'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.bonuses)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Bonuses', requisition.bonuses)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['overtime'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Overtime</label>
                            <label v-if="errors['overtime'] !== undefined" class="is-invalid-label">Overtime</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="overtime"
                                :value="(requisition) ? requisition.overtime : ''"
                                rows="5"
                            />
                            <div v-if="errors['overtime'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['overtime']" :key="'overtime'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.overtime)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Overtime', requisition.overtime)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>

                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['public_holidays_and_sundays'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Public Holidays & Sundays</label>
                            <label v-if="errors['public_holidays_and_sundays'] !== undefined" class="is-invalid-label">Public Holidays & Sundays</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="public_holidays_and_sundays"
                                :value="(requisition) ? requisition.public_holidays_and_sundays : ''"
                                rows="5"
                            />
                            <div v-if="errors['public_holidays_and_sundays'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['public_holidays_and_sundays']" :key="'public_holidays_and_sundays'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.public_holidays_and_sundays)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Public Holidays & Sundays', requisition.public_holidays_and_sundays)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>
                    <div class="grid-x grid-padding-x mb-4"  :class="this.gridPosition">
                        <div class="cell mb-3" :class="compact ? 'auto' : 'medium-6 large-6'">
                            <label v-if="errors['leave_policy'] == undefined" class="fw-normal" :class="(flow == 'readonly') ? 'readonly' : ''">Leave</label>
                            <label v-if="errors['leave_policy'] !== undefined" class="is-invalid-label">Leave</label>

                            <RedactorControl v-if="flow == 'write'"
                                name="leave_policy"
                                :value="(requisition) ? requisition.leave_policy : ''"
                                rows="5"
                            />
                            <div v-if="errors['leave_policy'] !== undefined" class="is-invalid-label">
                                <small v-for="error in errors['leave_policy']" :key="'leave_policy'+error"> {{ error }}</small><br/>
                            </div>
                            <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.leave_policy)"></div>
                        </div>

                        <div v-if="inputsClickable" class="cell shrink">
                            <div @click="() => $emit('input-clicked', 'Leave', requisition.leave_policy)" class="add-button pt-23">
                                <i class="fas fa-plus-circle"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly') ? 'readonly' : ''">Closing Date<sup>*</sup></label>

                <input name="closing_date" type="hidden" :value="closingDate" />
                <input :disabled="flow == 'readonly'" type="datetime-local" required :value="closingDate" @change="event => closingDate = event.target.value" />
            </div>

            <div v-if="inputsClickable" class="cell shrink mb-2">
                <div @click="() => $emit('input-clicked', 'Closing Date', closingDate)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly') ? 'readonly' : ''">Industries<sup>*</sup></label>
                <input v-if="industry" name="industry_id" type="hidden" :value="industry.id" />
                <vue-select
                    :disabled="flow == 'readonly'"
                    label="title"
                    :options="industries"
                    v-model="industry"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!industry"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                </vue-select>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Industry', requisition.industry.title)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-6'">
                <label :class="(flow == 'readonly') ? 'readonly' : ''">Extra Notes</label>

                <RedactorControl v-if="flow == 'write'"
                    name="notes"
                    :value="(requisition) ? requisition.notes : ''"
                    rows="5"
                />

                <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.notes)"></div>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Notes', requisition.notes)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="flow == 'readonly' ? 'readonly' : ''">Cost Center<sup>*</sup></label>

                <input v-if="costCenter" name="cost_center_id" type="hidden" :value="costCenter.id" />
                <vue-select
                    :disabled="flow == 'readonly'"
                    label="title"
                    @search="fetchCostCenters"
                    @open="fetchCostCenters"
                    :options="costCenters"
                    v-model="costCenter"
                 >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!costCenter"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                    <template slot="option" slot-scope="option">
                        {{ option.title }}
                    </template>
                </vue-select>

                <div v-if="errors['cost_center'] !== undefined" class="is-invalid-label">
                    <small v-for="error in errors['primary_recruiter_id']" :key="'primary_recruiter_id'+error"> {{ error }}</small><br/>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly') ? 'readonly' : ''">Primary Recruiter*</label>

                <input v-if="primaryRecruiter" name="primary_recruiter_id" type="hidden" :value="primaryRecruiter.id" />

                <vue-select
                    :disabled="flow == 'readonly'"
                    label="full_name"
                    @search="fetchUsers"
                    @open="fetchUsers"
                    :options="users"
                    v-model="primaryRecruiter"
                 >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!primaryRecruiter"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                    <template slot="option" slot-scope="option">
                        {{ option.full_name }} ({{ option.role }})
                    </template>
                </vue-select>

                <div v-if="errors['primary_recruiter_id'] !== undefined" class="is-invalid-label">
                    <small v-for="error in errors['primary_recruiter_id']" :key="'primary_recruiter_id'+error"> {{ error }}</small><br/>
                </div>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Primary Recruiter', primaryRecruiter.full_name)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-3 items-center">
            <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                <label :class="(flow == 'readonly') ? 'readonly' : ''">Senior Recruiter*</label>

                <input v-if="seniorRecruiter" name="senior_recruiter_id" type="hidden" :value="seniorRecruiter.id" />

                <vue-select
                    :disabled="flow == 'readonly'"
                    label="full_name"
                    @search="fetchUsers"
                    @open="fetchUsers"
                    :options="users"
                    v-model="seniorRecruiter"
                >
                    <template #search="{attributes, events}">
                        <input
                            class="vs__search"
                            :required="!seniorRecruiter"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                    <template slot="option" slot-scope="option">
                        {{ option.full_name }} ({{ option.role }})
                    </template>
                </vue-select>

                <div v-if="errors['senior_recruiter_id'] !== undefined" class="is-invalid-label">
                    <small v-for="error in errors['senior_recruiter_id']" :key="'senior_recruiter_id'+error"> {{ error }}</small><br/>
                </div>
            </div>

            <div v-if="inputsClickable" class="cell shrink">
                <div @click="() => $emit('input-clicked', 'Senior Recruiter', seniorRecruiter.full_name)" class="add-button pt-23">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>
        </div>

        <template v-if="!requisition.parent_id">
            <div class="grid-x grid-padding-x mb-4">
                <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                    <label :class="(errors['first_approver_id'] !== undefined) ? 'is-invalid-label' : ''">Approver #1*</label>

                    <input v-if="firstApprover" name="first_approver_id" type="hidden" :value="firstApprover.id" />

                    <vue-select
                        :disabled="flow == 'readonly'"
                        label="full_name"
                        @search="fetchUsers"
                        @open="fetchUsers"
                        :options="users"
                        v-model="firstApprover"
                    >
                        <template #search="{attributes, events}">
                            <input
                                class="vs__search"
                                :required="!firstApprover"
                                v-bind="attributes"
                                v-on="events"
                            />
                        </template>
                        <template slot="option" slot-scope="option">
                            {{ option.full_name }} ({{ option.role }})
                        </template>
                    </vue-select>
                    <div v-if="errors['first_approver_id'] !== undefined" class="is-invalid-label">
                        <small v-for="error in errors['first_approver_id']" :key="'first_approver_id'+error"> {{ error }}</small><br/>
                    </div>
                </div>
            </div>

            <div class="grid-x grid-padding-x mb-4">
                <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                    <label :class="(errors['second_approver_id'] !== undefined) ? 'is-invalid-label' : ''">Approver #2*</label>

                    <input v-if="secondApprover" name="second_approver_id" type="hidden" :value="secondApprover.id" />

                    <vue-select
                        :disabled="flow == 'readonly'"
                        label="full_name"
                        @search="fetchUsers"
                        @open="fetchUsers"
                        :options="users"
                        v-model="secondApprover"
                    >
                        <template #search="{attributes, events}">
                            <input
                                class="vs__search"
                                :required="!secondApprover"
                                v-bind="attributes"
                                v-on="events"
                            />
                        </template>
                        <template slot="option" slot-scope="option">
                            {{ option.full_name }} ({{ option.role }})
                        </template>
                    </vue-select>

                    <div v-if="errors['second_approver_id'] !== undefined" class="is-invalid-label">
                        <small v-for="error in errors['second_approver_id']" :key="'second_approver_id'+error"> {{ error }}</small><br/>
                    </div>
                </div>
            </div>

            <div class="grid-x grid-padding-x mb-4">
                <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                    <label :class="(errors['third_approver_id'] !== undefined) ? 'is-invalid-label' : ''">Approver #3</label>

                    <input v-if="thirdApprover" name="third_approver_id" type="hidden" :value="thirdApprover.id" />

                    <vue-select
                        :disabled="flow == 'readonly'"
                        label="full_name"
                        @search="fetchUsers"
                        @open="fetchUsers"
                        :options="users"
                        v-model="thirdApprover"
                    >
                        <template slot="option" slot-scope="option">
                            {{ option.full_name }} ({{ option.role }})
                        </template>
                    </vue-select>

                    <div v-if="errors['third_approver_id'] !== undefined" class="is-invalid-label">
                        <small v-for="error in errors['third_approver_id']" :key="'third_approver_id'+error"> {{ error }}</small><br/>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="requisition.parent_id != null && !hideStatus">
            <div class="grid-x grid-padding-x mb-4">
                <div class="cell" :class="compact ? 'auto' : 'medium-6 large-4'">
                    <label :class="(flow == 'readonly') ? 'readonly' : ''">Approver Comments</label>

                    <RedactorControl v-if="flow == 'write'"
                        name="approver_comment"
                        :value="(requisition) ? requisition.approver_comment : ''"
                        rows="5"
                    />

                <div v-if="flow == 'readonly'" class="html-content" v-html="removePTag(requisition.approver_comment)"></div>
                </div>
            </div>

            <p v-if="requisition.status" class="status">
                <i class="fa" :class="requisition.status == 'approved' ? 'fa-check' : 'fa-times'"></i>
                <strong>{{ requisition.status | capitalize }}</strong>
            </p>
        </template>

        <input v-if="csrfToken" type="hidden" name="_csrfToken" :value="csrfToken" />

        <div v-if="flow !== 'readonly'" class="grid-x grid-padding-x">
            <template v-if="!requisition.parent_id">
                <div v-if="requisition.created == null" class="cell shrink">
                    <button type="submit" ref="formSubmitButon" class="d-none"></button>
                    <button type="button" @click="submit" class="button primary">
                        Save Progress
                    </button>
                </div>

                <div class="cell shrink">
                    <input type="hidden" name="request_approval" :value="1"/>
                    <button type="submit" name="created" :value="now()" class="button success">
                        Submit For Approval
                    </button>
                </div>
            </template>

            <template v-else>
                <div class="cell shrink">
                    <input type="hidden" v-if="step === '1st'" name="approver_id_field" value="first_approver"/>
                    <input type="hidden" v-if="step === '2nd'" name="approver_id_field" value="second_approver"/>
                    <input type="hidden" v-if="step === 'Final'" name="approver_id_field" value="third_approver"/>
                    <button type="submit" name="status" value="approved" class="button success">
                        Approve
                    </button>
                </div>
                <div class="cell shrink">
                    <button type="submit" name="status" value="declined" class="button alert">
                        Decline
                    </button>
                </div>
            </template>

            <div class="cell shrink">
                <a href="/recruiter/requisitions/pending" class="button cancel">
                    Cancel
                </a>
            </div>
        </div>
    </form>
</template>

<style lang="scss">
.requisition-crud {
    .grid-x.items-center {
        align-items: center;
    }

    .vs__actions {
        margin-right: 6px;
    }
    .vs__clear {
        display: none;
    }

    .button {
        text-decoration: none;
    }

    .status {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 1rem;

        i {
            font-size: 36px;

            &.fa-check {
                color: yellowgreen;
            }

            &.fa-times {
                color: crimson;
            }
        }
    }

    .errors {
        border: 1px solid lightgray;
        padding: 1rem;
    }

    fieldset {
        margin-bottom: 1rem;

        &.bordered {
            border: 1px solid lightgray;
            padding: 1rem;
        }
    }

    .v-select, .vs__dropdown-toggle {
        background-color: white;
        height: 40px;
        
        input {
            background-color: transparent;
        }
    }
}
</style>